// @flow
import { Button, Label } from 'primitive-components';
import { Cell, Fill, Grid } from 'layout-core-components';
import { colors } from 'bench-style-constants';
import formatCurrency from 'format-currency';
import { formatDate, parse } from 'date-functions';
import {
  openSegmentCategoryManagerModal,
  SegmentAllocatorContainer,
} from 'segmented-revenue-components';
import React, { Component } from 'react';
import styled from 'styled-components';

import type { JournalEntry, SegmentCategories } from './types';
import TransactionSidebarBorderedSection from './TransactionSidebarBorderedSection';
import TransactionSidebarDetailsSlide from './TransactionSidebarDetailsSlide';
import TransactionSidebarSegmentationSlide from './TransactionSidebarSegmentationSlide';

const TransactionDescription = styled.span`
  word-break: break-all;
`;

const ScrollingSurface = styled.div`
  overflow-x: visible;
  overflow-y: auto;
  position: relative;
`;

const Spacer = styled.div`
  font-size: 0;
  height: 8px;
  line-height: 8px;
`;

const RelativeWrapper = styled.div`
  overflow: hidden;
  position: relative;

  & > * {
    height: 100%;
    width: 100%;
  }
`;

const StyledSlidingPanel = styled.div`
  background: ${colors.white};
  bottom: -56px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  transform: translateY(100%);
  transition: transform 0.15s ease-out;
  z-index: 1;

  ${({ isOpen }) =>
    isOpen &&
    `
    bottom: 0;
    transform: translateY(0);
    transition: transform 0.2s ease-in;
  `};

  & > * {
    height: 100%;
    width: 100%;
  }
`;

type TransactionSidebarProps = {
  attachedTo: string,
  clientId: string,
  conversationId: ?string,
  documentAttachedTo: ?string,
  documentConversationId: ?string,
  enableSegmenting: boolean,
  ledgerAccountId: ?string,
  onAllocationsRemoved: () => void,
  onFirstAllocationCreated: () => void,
  onSidebarClose: () => void,
  segmentCategories: ?Array<SegmentCategories>,
  transaction: JournalEntry,
};

type TransactionSidebarState = {
  isSegmenting: boolean,
};

export default class TransactionSidebar extends Component<
  TransactionSidebarProps,
  TransactionSidebarState,
> {
  static defaultProps = {
    conversationId: undefined,
    documentAttachedTo: undefined,
    documentConversationId: undefined,
    segmentCategories: [],
  };

  constructor(props: TransactionSidebarProps, context: Object) {
    super(props, context);
    this.state = {
      isSegmenting: window.localStorage.getItem('isSegmenting') || false,
    };
  }

  openSegmentation = () => {
    window.localStorage.setItem('isSegmenting', true);
    this.setState({ isSegmenting: true });
  };

  closeSegmentation = () => {
    window.localStorage.removeItem('isSegmenting');
    this.setState({ isSegmenting: false });
  };

  render() {
    const {
      attachedTo,
      clientId,
      conversationId,
      documentAttachedTo,
      documentConversationId,
      enableSegmenting,
      ledgerAccountId,
      onAllocationsRemoved,
      onFirstAllocationCreated,
      onSidebarClose,
      segmentCategories,
      transaction,
    } = this.props;

    const clientsWithEnabledSegmentTracking =
      window?.BENCH?.clientsWithEnabledSegmentTracking || {};
    const transactionTaggingDisabled = clientsWithEnabledSegmentTracking[clientId] !== true;

    /**
     * Handle ledgerAccountId being optional to the TransactionSidebar
     * (as is the case when viewing a transaction via searching).
     */
    const ledgerAccountIdIsSet = ledgerAccountId !== undefined && ledgerAccountId !== null;

    if (enableSegmenting && !ledgerAccountIdIsSet) {
      throw new Error(
        `No data entry to segment found in transaction for matching ledger ${String(
          ledgerAccountId,
        )}`,
      );
    }

    return (
      <Fill to="parent">
        <Grid column>
          <Cell size="min-content">
            <TransactionSidebarBorderedSection withPadding>
              <Grid row="auto min-content">
                <div />
                <Button subtle styleType="neutral" iconRight="close" onClick={onSidebarClose}>
                  {' '}
                </Button>
              </Grid>
              <Grid rowSpacing="medium" columnSpacing="medium" row="auto min-content">
                <Label data-test-id="sidebar-transactionDate">
                  {formatDate(parse(transaction.dateOfTransaction), {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Label>
                <Label>Transaction&nbsp;Total</Label>
              </Grid>
              <Spacer />
              <Grid rowSpacing="medium" columnSpacing="medium" row="auto max-content">
                <TransactionDescription data-test-id="sidebar-transactionVendor">
                  {transaction.description}
                </TransactionDescription>
                <span data-test-id="sidebar-transactionAmount">
                  {formatCurrency(transaction.totalAmount / 100)}
                </span>
              </Grid>
            </TransactionSidebarBorderedSection>
          </Cell>
          <Cell size="auto" style={{ position: 'relative' }}>
            <RelativeWrapper>
              <ScrollingSurface>
                <Fill to="parent">
                  <TransactionSidebarDetailsSlide
                    clientId={clientId}
                    documentConversationId={documentConversationId}
                    documentAttachedTo={documentAttachedTo}
                    conversationId={conversationId}
                    attachedTo={attachedTo}
                    transaction={transaction}
                    currentLedgerId={ledgerAccountId}
                  />
                </Fill>
              </ScrollingSurface>
              {enableSegmenting && (
                <StyledSlidingPanel isOpen={this.state.isSegmenting}>
                  <ScrollingSurface>
                    <Fill to="parent">
                      {ledgerAccountIdIsSet && (
                        <TransactionSidebarSegmentationSlide
                          hasSegmentCategories={!!(segmentCategories && segmentCategories.length)}
                          onRequestOpenManageSegments={() =>
                            openSegmentCategoryManagerModal(clientId)
                          }
                          onViewEntireTransaction={this.closeSegmentation}
                          clientId={clientId}
                        >
                          <div style={{ padding: '1rem 1.5rem' }}>
                            {' '}
                            <SegmentAllocatorContainer
                              clientId={clientId}
                              dataEntries={transaction.dataEntries}
                              ledgerAccountId={ledgerAccountId}
                              ledgerName={
                                transaction.ledgerAccount ? transaction.ledgerAccount.name : ''
                              }
                              onAllocationsRemoved={onAllocationsRemoved}
                              onFirstAllocationCreated={onFirstAllocationCreated}
                              segmentCategories={segmentCategories}
                              transactionId={transaction.id}
                            />
                          </div>
                        </TransactionSidebarSegmentationSlide>
                      )}
                    </Fill>
                  </ScrollingSurface>
                </StyledSlidingPanel>
              )}
            </RelativeWrapper>
          </Cell>
          {enableSegmenting && ledgerAccountIdIsSet && !this.state.isSegmenting ? (
            <Cell size="56px">
              <TransactionSidebarBorderedSection>
                <Fill to="parent">
                  <Button
                    subtle
                    iconRight="chevronUp"
                    onClick={this.openSegmentation}
                    disabled={transactionTaggingDisabled}
                  >
                    Tag This Transaction
                  </Button>
                </Fill>
              </TransactionSidebarBorderedSection>
            </Cell>
          ) : (
            <Cell size="0px">&nbsp;</Cell>
          )}
        </Grid>
      </Fill>
    );
  }
}
